/**
 * metaLeadGen/service.ts
 *
 * Main service layer. All UI should call these functions — never touch raw data directly.
 *
 * To connect live Meta API data:
 * 1. Replace RAW_META_LEAD_GEN_ROWS with adaptMetaApiResponse(apiRows) from the adapter
 * 2. No other changes needed
 */

import type { MetaLeadGenRow, MetaLeadGenFilters, MetaLeadGenSummary, PeriodComparison, FilterOptions } from "./types";
import { RAW_META_LEAD_GEN_ROWS } from "./mockData";
import { applyFilters, getFilterOptions, getPreviousPeriodRows } from "./filters";
import { aggregateTotals, calculateMetrics, comparePeriods } from "./calculations";

const DEFAULT_CURRENCY = "USD";

// ─────────────────────────────────────────────
// GET SUMMARY — aggregate + calculate metrics for a given filter set
// Returns clean zero-state if no rows match.
// ─────────────────────────────────────────────
export function getMetaLeadGenData(
  filters: MetaLeadGenFilters = {},
  rows: MetaLeadGenRow[] = RAW_META_LEAD_GEN_ROWS
): MetaLeadGenSummary {
  const filtered = applyFilters(rows, filters);
  const totals = aggregateTotals(filtered);
  const metrics = calculateMetrics(totals);

  return {
    currencyCode: DEFAULT_CURRENCY,
    metrics,
    rowCount: filtered.length,
  };
}

// ─────────────────────────────────────────────
// GET COMPARISON — current period vs previous period (same duration, shifted back)
// Requires dateRange in filters.
// ─────────────────────────────────────────────
export function getMetaLeadGenComparison(
  filters: MetaLeadGenFilters & { dateRange: { start: string; end: string } },
  rows: MetaLeadGenRow[] = RAW_META_LEAD_GEN_ROWS
): PeriodComparison {
  const currentRows = applyFilters(rows, filters);

  // Previous period rows — same date duration, shifted back
  const prevDateRows = getPreviousPeriodRows(rows, filters.dateRange);
  // Apply same non-date filters to previous period rows
  const { dateRange: _ignored, ...nonDateFilters } = filters;
  const previousRows = applyFilters(prevDateRows, nonDateFilters);

  return comparePeriods(currentRows, previousRows);
}

// ─────────────────────────────────────────────
// GET FILTER OPTIONS — unique values in data (optionally scoped by existing filters)
// ─────────────────────────────────────────────
export function getMetaLeadGenFilterOptions(
  filters: MetaLeadGenFilters = {},
  rows: MetaLeadGenRow[] = RAW_META_LEAD_GEN_ROWS
): FilterOptions {
  const filtered = applyFilters(rows, filters);
  return getFilterOptions(filtered);
}
